import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { Configuration, ConfigurationDetail, ConfigurationOption, ConfiguratorType, LiftSystem, Project } from './types';

const normalizedApiOrigin = process.env.NEXT_PUBLIC_API_URL?.trim().replace(/\/+$/, '');
const API_BASE_URL = normalizedApiOrigin ? `${normalizedApiOrigin}/api` : '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies with cross-origin requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Retry helper for API calls
async function retryOnNetworkError<T>(
  fn: () => Promise<T>,
  retryDelay: number = 500
): Promise<T> {
  try {
    return await fn();
  } catch (error: any) {
    // Check if it's a network/API error (not a business logic error like calculation errors)
    const isNetworkError = 
      !error.response || // Network error (no response)
      error.response.status >= 500 || // Server error
      error.code === 'ECONNABORTED' || // Timeout
      error.code === 'ERR_NETWORK'; // Generic network error
    
    if (isNetworkError) {
      console.log(`Network error detected, retrying in ${retryDelay}ms...`, error);
      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return await fn(); // Single retry
    }
    
    // If not a network error, throw immediately
    throw error;
  }
}

// Types
interface User {
  id: string;
  name: string;
  email: string;
  position: string;
  role: 'user' | 'admin';
  company: {
    id: string;
    name: string;
    erpNumber?: string;
  };
}

interface LoginResponse {
  user: User;
  requiresConsent?: boolean;
  requirements?: Array<{
    docType: 'terms' | 'privacy';
    version: string;
    title: string;
    contentHtml: string;
  }>;
  access_token?: string;
}

interface UserSetting {
  id: string;
  configurator_type: string;
  price_multiplier: number;
  enabled: boolean;
}

interface UserSettingsResponse {
  user_id: string;
  settings: UserSetting[];
}

// Auth API calls
export const authAPI = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  submitConsent: async (data: {
    email: string;
    password: string;
    consents: Array<{ docType: 'terms'; version: string; penaltyNoticeAck: true }>;
    privacyAccepted: boolean;
  }): Promise<LoginResponse> => {
    const response = await api.post('/auth/legal/consent', data);
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (
    token: string,
    password: string,
    passwordConfirmation: string
  ): Promise<{ message: string }> => {
    const response = await api.post('/auth/reset-password', {
      token,
      password,
      passwordConfirmation,
    });
    return response.data;
  },

  logout: async (): Promise<{ message: string }> => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/auth/profile');
    return response.data;
  },

  updateProfile: async (data: { name?: string; position?: string }): Promise<User> => {
    const response = await api.put('/auth/profile', data);
    return response.data;
  },

  validateInvitation: async (token: string): Promise<{
    email: string;
    company: { id: string; name: string } | null;
    role: string;
  }> => {
    const response = await api.get(`/auth/invitation/${token}`);
    return response.data;
  },

  register: async (
    token: string,
    name: string,
    password: string,
    passwordConfirmation: string,
    position?: string
  ): Promise<{ message: string; user: User }> => {
    const response = await api.post('/auth/register', {
      token,
      name,
      password,
      passwordConfirmation,
      ...(position ? { position } : {}),
    });
    return response.data;
  },
};

export const legalAPI = {
  getActive: async (docType: 'terms' | 'privacy'): Promise<{
    docType: 'terms' | 'privacy';
    version: string;
    title: string;
    contentHtml: string;
  }> => {
    const response = await api.get(`/legal/documents/${docType}/active`)
    return response.data
  },
  downloadActive: (docType: 'terms' | 'privacy') => {
    const url = `${API_BASE_URL}/legal/documents/${docType}/active/download`
    window.open(url, '_blank')
  }
}

// Company API calls
export const companyAPI = {
  getCompany: async (companyId: string): Promise<{
    id: string;
    name: string;
    erpNumber: string;
    users: Array<{
      id: string;
      name: string;
      email: string;
      position: string;
    }>;
    createdAt: string;
    updatedAt: string;
  }> => {
    const response = await api.get(`/companies/${companyId}`);
    return response.data;
  },

  getDefaultSettings: async (): Promise<Array<{
    configurator_type: string;
    price_multiplier: number;
    enabled: boolean;
  }>> => {
    const response = await api.get('/company/settings/defaults');
    return response.data;
  },
};

// User Settings API calls
export const userSettingsAPI = {
  getSettings: async (): Promise<UserSettingsResponse> => {
    const response = await api.get('/user/settings');
    return response.data;
  },

  updateSettings: async (settings: Array<{
    id: string;
    price_multiplier: number;
    enabled: boolean;
  }>): Promise<UserSettingsResponse> => {
    const response = await api.put('/user/settings', { settings });
    return response.data;
  },

  resetToDefaults: async (): Promise<UserSettingsResponse> => {
    const response = await api.post('/user/settings/reset-to-defaults');
    return response.data;
  },

  updateProfile: async (data: { name?: string; position?: string }): Promise<User> => {
    const response = await api.patch('/user/profile', data);
    return response.data;
  },
};

// Presentation Mode API calls
export const presentationModeAPI = {
  getStatus: async (): Promise<{ isPresentation: boolean }> => {
    const response = await api.get('/user/presentation-mode');
    return response.data;
  },

  toggleStatus: async (isPresentation: boolean): Promise<{ isPresentation: boolean }> => {
    const response = await api.post('/user/presentation-mode', { isPresentation });
    return response.data;
  },
};

// Support API calls
export const supportAPI = {
  submitRequest: async (data: {
    subject: string;
    message: string;
    priority?: 'Low' | 'Medium' | 'High';
  }): Promise<{
    id: string;
    message: string;
  }> => {
    const response = await api.post('/support', data);
    return response.data;
  },

  getRequests: async (): Promise<{
    support_requests: Array<{
      id: string;
      subject: string;
      message: string;
      status: 'Open' | 'In Progress' | 'Resolved';
      priority: 'Low' | 'Medium' | 'High';
      created_at: string;
      updated_at: string;
      user: {
        name: string;
        email: string;
      };
      handler?: {
        name: string;
        email: string;
      };
    }>;
  }> => {
    const response = await api.get('/support');
    return response.data;
  },

  updateRequest: async (
    id: string,
    updates: {
      status?: 'Open' | 'In Progress' | 'Resolved';
      priority?: 'Low' | 'Medium' | 'High';
    }
  ): Promise<{
    message: string;
    support_request: any;
  }> => {
    const response = await api.put(`/support/${id}`, updates);
    return response.data;
  },
};

// Admin API calls for configurator access management
export const adminConfiguratorAccessAPI = {
  getUserAccess: async (userId: string): Promise<{
    user_id: string;
    user_name: string;
    user_email: string;
    settings: Array<{
      id: string | null;
      configurator_type: string;
      configurator_type_id: string;
      price_multiplier: number;
      enabled: boolean;
      admin_enabled: boolean;
    }>;
  }> => {
    const response = await api.get(`/admin/users/${userId}/configurator-access`);
    return response.data;
  },

  updateUserAccess: async (userId: string, settings: Array<{
    configurator_type: string;
    admin_enabled: boolean;
  }>): Promise<{ message: string }> => {
    const response = await api.put(`/admin/users/${userId}/configurator-access`, { settings });
    return response.data;
  },
};

// Template API calls
export const templateAPI = {
  getTemplates: async (configuratorTypeId: string): Promise<{
    templates: {
      user: Array<{
        id: string;
        configurator_type_id: string;
        company_id: string | null;
        user_id: string | null;
        name: string;
        description: string | null;
        data: any;
        created_at: string;
        updated_at: string;
      }>;
      company: Array<{
        id: string;
        configurator_type_id: string;
        company_id: string | null;
        user_id: string | null;
        name: string;
        description: string | null;
        data: any;
        created_at: string;
        updated_at: string;
      }>;
    };
  }> => {
    const response = await api.get('/templates', { 
      params: { configurator_type_id: configuratorTypeId } 
    });
    return response.data;
  },

  getTemplate: async (templateId: string): Promise<{
    id: string;
    configurator_type_id: string;
    company_id: string | null;
    user_id: string | null;
    name: string;
    description: string | null;
    data: any;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.get(`/templates/${templateId}`);
    return response.data;
  },

  createTemplate: async (data: {
    name: string;
    description?: string;
    configurator_type_id: string;
    data: any;
    scope: 'user' | 'company';
  }): Promise<{
    id: string;
    configurator_type_id: string;
    company_id: string | null;
    user_id: string | null;
    name: string;
    description: string | null;
    data: any;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.post('/templates', data);
    return response.data;
  },

  updateTemplate: async (templateId: string, data: {
    name?: string;
    description?: string;
    data?: any;
  }): Promise<{
    id: string;
    configurator_type_id: string;
    company_id: string | null;
    user_id: string | null;
    name: string;
    description: string | null;
    data: any;
    created_at: string;
    updated_at: string;
  }> => {
    const response = await api.put(`/templates/${templateId}`, data);
    return response.data;
  },

  deleteTemplate: async (templateId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/templates/${templateId}`);
    return response.data;
  },
};

// Project API calls
export const projectAPI = {
  getProjects: async (params?: { all?: boolean }): Promise<{ projects: Project[] }> => {
    const response = await api.get('/projects', { params: params?.all ? { all: true } : undefined });
    return response.data;
  },

  createProject: async (project: {
    name: string;
    projectNumber?: string;
    address: string;
    deadline: string;
    status?: 'Active' | 'Pending' | 'Won' | 'Lost' | 'Archived';
  }): Promise<Project> => {
    const response = await api.post('/projects', project);
    return response.data;
  },

  updateProject: async (
    projectId: string,
    projectData: {
      name?: string;
      projectNumber?: string;
      address?: string;
      deadline?: string;
      status?: 'Active' | 'Pending' | 'Won' | 'Lost' | 'Archived';
    }
  ): Promise<Project> => {
    const response = await api.put(`/projects/${projectId}`, projectData);
    return response.data;
  },

  deleteProject: async (projectId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/projects/${projectId}`);
    return response.data;
  },
  recalculateProjectDistances: async (projectId: string): Promise<{ message: string; written: number }> => {
    const response = await api.post(`/projects/${projectId}/recalculate-distances`)
    return response.data
  },
};

// Lift System API calls
export const liftSystemAPI = {
  getLiftSystems: async (projectId: string): Promise<{ liftSystems: LiftSystem[] }> => {
    const response = await api.get(`/projects/${projectId}/liftsystems`);
    return response.data;
  },

  addLiftSystem: async (projectId: string, name: string): Promise<LiftSystem> => {
    const response = await api.post(`/projects/${projectId}/liftsystems`, { name });
    return response.data;
  },

  updateLiftSystem: async (
    projectId: string,
    liftSystemId: string,
    name: string
  ): Promise<LiftSystem> => {
    const response = await api.put(`/projects/${projectId}/liftsystems/${liftSystemId}`, { name });
    return response.data;
  },

  deleteLiftSystem: async (projectId: string, liftSystemId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/projects/${projectId}/liftsystems/${liftSystemId}`);
    return response.data;
  },
};

// Configuration API calls
export const configurationAPI = {
  getConfigurations: async (
    projectId: string,
    params?: { liftSystemId?: string; configuratorTypeId?: string }
  ): Promise<{ configurations: Configuration[] }> => {
    const response = await api.get(`/projects/${projectId}/configurations`, { params });
    return response.data;
  },

  getConfigurationDetails: async (configId: string): Promise<ConfigurationDetail> => {
    const response = await api.get(`/configurations/${configId}`);
    return response.data;
  },

  addConfiguration: async (
    projectId: string,
    config: {
      name: string;
      liftSystemId: string;
      configuratorTypeId: string;
      quantity?: number;
      assemblyPhase?: number;
    }
  ): Promise<Configuration> => {
    const response = await api.post(`/projects/${projectId}/configurations`, config);
    return response.data;
  },

  updateConfiguration: async (
    projectId: string,
    configId: string,
    configData: {
      name?: string;
      quantity?: number;
      assemblyPhase?: number;
    }
  ): Promise<Configuration> => {
    const response = await api.put(`/projects/${projectId}/configurations/${configId}`, configData);
    return response.data;
  },

  // Update configuration quantity with batch recalculation for configurator
  updateConfigurationQuantityWithRecalculation: async (
    projectId: string,
    configId: string,
    quantity: number
  ): Promise<{
    message: string;
    updated_configuration: Configuration;
    recalculation_results: Array<{
      config_id: string;
      success: boolean;
      total_quantity_used?: number;
      error?: string;
      note?: string;
    }>;
    affected_configurations_count: number;
  }> => {
    return retryOnNetworkError(async () => {
      const response = await api.put(
        `/projects/${projectId}/configurations/${configId}/update-quantity-with-recalculation`, 
        { quantity }
      );
      return response.data;
    });
  },

  deleteConfiguration: async (projectId: string, configId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/projects/${projectId}/configurations/${configId}`);
    return response.data;
  },

  saveConfigurationData: async (
    configId: string,
    data: { options: ConfigurationOption[] }
  ): Promise<{ message: string }> => {
    const response = await api.post(`/configurations/${configId}/data`, data);
    return response.data;
  },

  duplicateConfiguration: async (
    configId: string,
    data?: {
      name?: string;
      projectId?: string;
      liftSystemId?: string;
    }
  ): Promise<Configuration> => {
    const response = await api.post(`/configurations/${configId}/duplicate`, data || {});
    return response.data;
  },
};

// Configurator Types API calls
export const configuratorTypesAPI = {
  getConfiguratorTypes: async (): Promise<{ configuratorTypes: ConfiguratorType[] }> => {
    const response = await api.get('/configurator-types');
    return response.data;
  },
};

// Calculator API calls
export const calculatorAPI = {
  calculatePrices: async (configurationId: string): Promise<{
    configuration_id: string;
    price_results: Record<string, number>;
    total_price: number;
    updated_options?: Array<{ option_id: string; price: number }>;
  }> => {
    return retryOnNetworkError(async () => {
      // Use the same working endpoint that getAdditionalOptionPrices uses
      const response = await api.post(`/configurations/${configurationId}/calculate-prices`);
      return response.data;
    });
  },
  
  getAdditionalOptionPrices: async (configId: string): Promise<{
    cadDrawing: number;
    shaftClosingAngle: number;
    perimeterSealing: number;
    thresholdPlates: number;
    conicalManufacturing: number;
    ledSpots: number;
    ledLightStrip: number;
    neoEdge: number;
  }> => {
    // Use the endpoint with the config_id in the path
    const response = await api.post(`/configurations/${configId}/calculate-prices`);
    
    // Parse the response data from the updated format
    const { updated_options = [], total_price = 0 } = response.data;
    
    // Create a map of option_id to price from the updated_options array
    const priceMap = updated_options.reduce((acc: Record<string, number>, option: { option_id: string; price: number }) => {
      acc[option.option_id] = option.price;
      return acc;
    }, {});
    
    // Default prices in case options are not found
    const defaultPrices = {
      cadDrawing: 350,
      shaftClosingAngle: 180,
      perimeterSealing: 120,
      thresholdPlates: 200,
      conicalManufacturing: 250,
      ledSpots: 320,
      ledLightStrip: 450,
      neoEdge: 150
    };
    
    // Return the mapped prices with defaults for missing options
    return {
      cadDrawing: priceMap['cad_drawing'] || defaultPrices.cadDrawing,
      shaftClosingAngle: priceMap['shaft_closing_angle'] || defaultPrices.shaftClosingAngle,
      perimeterSealing: priceMap['perimeter_sealing'] || defaultPrices.perimeterSealing,
      thresholdPlates: priceMap['threshold_plates'] || defaultPrices.thresholdPlates,
      conicalManufacturing: priceMap['conical_manufacturing'] || defaultPrices.conicalManufacturing,
      ledSpots: priceMap['led_spots'] || defaultPrices.ledSpots,
      ledLightStrip: priceMap['led_light_strip'] || defaultPrices.ledLightStrip,
      neoEdge: priceMap['neo_edge'] || defaultPrices.neoEdge
    };
  }
};

// Admin API calls
export const adminAPI = {
  // Company management
  getCompanies: async (): Promise<{
    companies: Array<{
      id: string;
      name: string;
      erp_number?: string;
      created_at: string;
      updated_at: string;
      user_count: number;
    }>;
  }> => {
    const response = await api.get('/admin/companies');
    return response.data;
  },

  createCompany: async (data: {
    name: string;
    erp_number?: string;
  }): Promise<{
    message: string;
    company: {
      id: string;
      name: string;
      erp_number?: string;
      created_at: string;
      updated_at: string;
    };
  }> => {
    const response = await api.post('/admin/companies', data);
    return response.data;
  },

  updateCompany: async (
    companyId: string,
    data: {
      name?: string;
      erp_number?: string;
      price_factor?: number;
    }
  ): Promise<{
    message: string;
    company: {
      id: string;
      name: string;
      erp_number?: string;
      price_factor?: number;
      created_at: string;
      updated_at: string;
    };
  }> => {
    const response = await api.put(`/admin/companies/${companyId}`, data);
    return response.data;
  },

  deleteCompany: async (companyId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/companies/${companyId}`);
    return response.data;
  },

  // User management
  getUsers: async (): Promise<{
    users: Array<User>;
  }> => {
    const response = await api.get('/admin/users');
    return response.data;
  },

  createUser: async (data: {
    name: string;
    email: string;
    password: string;
    position?: string;
    company_id?: string;
    status?: 'active' | 'blocked' | 'archived';
    role?: 'user' | 'admin';
  }): Promise<{
    message: string;
    user: User;
  }> => {
    const response = await api.post('/admin/users', data);
    return response.data;
  },

  updateUser: async (
    userId: string,
    data: {
      name?: string;
      email?: string;
      password?: string;
      position?: string;
      company_id?: string;
      status?: 'active' | 'blocked' | 'archived';
      role?: 'user' | 'admin';
    }
  ): Promise<{
    message: string;
    user: User;
  }> => {
    const response = await api.put(`/admin/users/${userId}`, data);
    return response.data;
  },

  deleteUser: async (userId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },

  // Company price multiplier management
  getCompanyPriceMultipliers: async (companyId: string): Promise<{
    company_id: string;
    company_name: string;
    multipliers: Array<{
      id: string | null;
      configurator_type_id: string;
      configurator_type_name: string;
      price_multiplier: number;
      enabled: boolean;
    }>;
  }> => {
    const response = await api.get(`/admin/companies/${companyId}/price-multipliers`);
    return response.data;
  },

  updateCompanyPriceMultipliers: async (
    companyId: string,
    data: {
      multipliers: Array<{
        id?: string | null;
        configurator_type_id: string;
        price_multiplier: number;
        enabled: boolean;
      }>;
    }
  ): Promise<{ message: string }> => {
    const response = await api.put(`/admin/companies/${companyId}/price-multipliers`, data);
    return response.data;
  },

  // Invitation management
  createInvitation: async (data: {
    email: string;
    company_id?: string;
    configurator_access?: Array<{ type_id: string; admin_enabled: boolean }>;
  }): Promise<{
    message: string;
    invitation: {
      id: string;
      email: string;
      role: string;
      expires_at: string;
      used: boolean;
      company: { id: string; name: string } | null;
      link: string;
    };
  }> => {
    const response = await api.post('/admin/invitations', data);
    return response.data;
  },

  regenerateInvitation: async (data: {
    email: string;
    company_id?: string;
    configurator_access?: Array<{ type_id: string; admin_enabled: boolean }>;
  }): Promise<{
    message: string;
    invitation: {
      id: string;
      email: string;
      role: string;
      expires_at: string;
      used: boolean;
      company: { id: string; name: string } | null;
      link: string;
    };
  }> => {
    const response = await api.post('/admin/invitations/regenerate', data);
    return response.data;
  },

  sendInvitationEmail: async (invitationId: string): Promise<{ message: string }> => {
    const response = await api.post(`/admin/invitations/${invitationId}/send-email`);
    return response.data;
  },

  getInvitations: async (): Promise<{
    invitations: Array<{
      id: string;
      email: string;
      role: string;
      expires_at: string;
      used: boolean;
      created_at: string;
      company: { id: string; name: string } | null;
      is_expired: boolean;
    }>;
  }> => {
    const response = await api.get('/admin/invitations');
    return response.data;
  },

  // Statistics endpoints
  getOverviewStats: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
  }): Promise<{
    active_users: number;
    total_companies: number;
    active_projects: number;
    today_configurations: number;
    pending_orders: number;
    total_revenue: number;
  }> => {
    const response = await api.get('/admin/stats/overview', { params });
    return response.data;
  },

  getUserActivityStats: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    user_id?: string;
  }): Promise<{
    total_users: number;
    active_users_24h: number;
    active_users_7d: number;
    active_users_30d: number;
    user_status_counts: Array<{ status: string; count: number }>;
    top_users: Array<{ 
      name: string; 
      email: string; 
      session_count: number;
      project_count: number;
      recent_projects_30d: number;
      order_count: number;
    }>;
    user_growth_trend: Array<{ date: string; new_users: number }>;
    cumulative_users: Array<{ date: string; cumulative_users: number }>;
    latest_new_users: Array<{ name: string; email: string; created_at: string }>;
    total_config_value: number;
    total_order_value: number;
    config_to_order_conversion_rate: number;
    order_acceptance_rate: number;
    total_orders: number;
    accepted_orders: number;
  }> => {
    const response = await api.get('/admin/stats/user-activity', { params });
    return response.data;
  },

  getCompanyInsights: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    accepted_only?: string;
  }): Promise<{
    total_companies: number;
    configs_per_company: Array<{ company: string; count: number }>;
    avg_config_value_per_company: Array<{ company: string; avg_price: number; config_count: number }>;
    most_active_companies: Array<{ company: string; total_value: number; order_count: number }>;
  }> => {
    const response = await api.get('/admin/stats/company-insights', { params });
    return response.data;
  },

  getConfigurationMetrics: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    user_id?: string;
    configurator_type?: string;
  }): Promise<{
    total_configurations: number;
    daily_configurations: Array<{ date: string; count: number }>;
    avg_configuration_price: number;
    configurator_type_distribution: Array<{ type: string; count: number }>;
    high_value_configurations: Array<{
      id: string;
      name: string;
      price: number;
      type: string;
      created_at: string;
    }>;
  }> => {
    const response = await api.get('/admin/stats/configuration-metrics', { params });
    return response.data;
  },

  getProjectOverview: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    user_id?: string;
  }): Promise<{
    daily_projects: Array<{ date: string; count: number }>;
    project_status_breakdown: Array<{ status: string; count: number }>;
    projects_near_deadline: Array<{
      id: string;
      name: string;
      deadline: string;
      status: string;
    }>;
    projects_per_company: Array<{ company: string; project_count: number }>;
  }> => {
    const response = await api.get('/admin/stats/project-overview', { params });
    return response.data;
  },

  getOrderFunnel: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    user_id?: string;
    configurator_type?: string;
  }): Promise<{
    daily_orders: Array<{ date: string; count: number }>;
    order_status_breakdown: Array<{ status: string; count: number }>;
    accepted_order_value: Array<{ date: string; total_value: number }>;
    conversion_rate: number;
    avg_order_value: number;
    order_trend: number;
    current_period_orders: number;
    previous_period_orders: number;
    // NEW METRICS
    total_configuration_value: number;
    total_order_value: number;
    total_accepted_order_value: number;
    order_acceptance_rate: number;
    order_to_config_value_ratio: number;
    config_to_accepted_order_value_ratio: number;
  }> => {
    const response = await api.get('/admin/stats/order-funnel', { params });
    return response.data;
  },

  getAuthMetrics: async (params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{
    session_metrics: {
      total_sessions: number;
      active_sessions: number;
      avg_session_duration_hours: number;
      daily_sessions: Array<{ date: string; count: number }>;
    };
    login_attempts: {
      total_attempts: number;
      successful_attempts: number;
      failed_attempts: number;
      success_rate: number;
      daily_attempts: Array<{
        date: string;
        total_attempts: number;
        successful_attempts: number;
        failed_attempts: number;
      }>;
    };
  }> => {
    const response = await api.get('/admin/stats/auth-metrics', { params });
    return response.data;
  },

  getDataQualityStats: async (): Promise<{
    orphaned_configurations: number;
    configurations_without_orders: number;
    duplicate_configurations: number;
    orders_with_mismatched_totals: number;
    users_without_companies: number;
    duplicate_config_details: Array<{
      project_id: string;
      name: string;
      count: number;
    }>;
  }> => {
    const response = await api.get('/admin/stats/data-quality');
    return response.data;
  },

  exportStatistics: async (params: {
    type: 'overview' | 'user-activity' | 'company-insights' | 'configuration-metrics' | 'project-overview' | 'order-funnel' | 'data-quality';
    format?: 'json' | 'csv';
    start_date?: string;
    end_date?: string;
  }) => {
    const response = await api.get('/admin/stats/export', { params });
    return response.data;
  },

  getSupportMetrics: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    user_id?: string;
  }): Promise<{
    total_support_requests: number;
    open_requests: number;
    resolved_requests: number;
    avg_resolution_hours: number;
    support_status_breakdown: Array<{ status: string; count: number }>;
    daily_support_requests: Array<{ date: string; count: number }>;
  }> => {
    const response = await api.get('/admin/stats/support-metrics', { params });
    return response.data;
  },

  getSystemHealth: async (params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{
    api_metrics: {
      total_requests: number;
      avg_response_time_ms: number;
      error_rate: number;
      daily_requests: Array<{ date: string; count: number }>;
      popular_endpoints: Array<{ endpoint: string; count: number }>;
      error_breakdown: Array<{ status_code: number; count: number }>;
    };
    database_status: string;
    uptime_status: string;
  }> => {
    const response = await api.get('/admin/stats/system-health', { params });
    return response.data;
  },

  getCompanyValueTrends: async (params?: {
    start_date?: string;
    end_date?: string;
    company_id?: string;
    accepted_only?: string;
  }): Promise<{
    configuration_values: Array<{ date: string; company: string; value: number }>;
    order_values: Array<{ date: string; company: string; value: number }>;
    accepted_order_values: Array<{ date: string; company: string; value: number }>;
    companies: string[];
  }> => {
    const response = await api.get('/admin/stats/company-value-trends', { params });
    return response.data;
  },

  resetErrorLogs: async (): Promise<{
    message: string;
    deleted_count: number;
    errors_before_reset: number;
  }> => {
    const response = await api.delete('/admin/stats/reset-error-logs');
    return response.data;
  },

  // Calculation Error Snapshots
  getCalculationErrors: async (params?: {
    page?: number;
    per_page?: number;
    error_type?: string;
    configurator_type?: string;
  }): Promise<{
    errors: Array<{
      id: string;
      created_at: string;
      user_id: string | null;
      user_name: string | null;
      configuration_id: string;
      project_id: string;
      error_type: string;
      error_details: Record<string, unknown> | null;
      project_name: string | null;
      configuration_name: string | null;
      configurator_type: string | null;
    }>;
    pagination: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  }> => {
    const response = await api.get('/admin/calculation-errors', { params });
    return response.data;
  },

  getCalculationErrorDetail: async (errorId: string): Promise<{
    id: string;
    created_at: string;
    user_id: string | null;
    user_name: string | null;
    configuration_id: string;
    project_id: string;
    error_type: string;
    error_details: Record<string, unknown> | null;
    snapshot_data: {
      project: {
        id: string;
        name: string;
        address: string;
        distance: number | null;
        deadline: string | null;
        project_number: string | null;
      };
      configuration: {
        id: string;
        name: string | null;
        configurator_type: string;
        quantity: number;
        assembly_phase: number;
        lift_system_id: string;
      };
      options: Record<string, unknown>;
      calculated_values: Record<string, unknown>;
      aggregated_values: Record<string, unknown>;
    };
  }> => {
    const response = await api.get(`/admin/calculation-errors/${errorId}`);
    return response.data;
  },

  downloadCalculationErrorCSV: async (errorId: string): Promise<void> => {
    const response = await api.get(`/admin/calculation-errors/${errorId}/csv`, {
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `calculation_error_${errorId.substring(0, 8)}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  downloadAllCalculationErrorsCSV: async (params?: {
    error_type?: string;
    configurator_type?: string;
  }): Promise<void> => {
    const response = await api.get('/admin/calculation-errors/csv', {
      params,
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `calculation_errors_export.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  deleteCalculationError: async (errorId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/admin/calculation-errors/${errorId}`);
    return response.data;
  },

  bulkDeleteCalculationErrors: async (ids: string[]): Promise<{
    message: string;
    deleted_count: number;
  }> => {
    const response = await api.post('/admin/calculation-errors/bulk-delete', { ids });
    return response.data;
  },

  deleteAllCalculationErrors: async (params?: {
    error_type?: string;
    configurator_type?: string;
  }): Promise<{
    message: string;
    deleted_count: number;
  }> => {
    const response = await api.delete('/admin/calculation-errors/delete-all', { params });
    return response.data;
  },
};

// Operator Locations (Admin)
export const operatorLocationsAPI = {
  list: async (): Promise<{ locations: Array<{
    id: string;
    name: string;
    address: string;
    is_active: boolean;
    service_radius_km: number | null;
    is_main_hub: boolean;
    main_hub_distance_km: number | null;
    configurator_types: string[];
  }>} > => {
    const response = await api.get('/operator/locations')
    return response.data
  },
  create: async (data: {
    name: string;
    address: string;
    is_active?: boolean;
    service_radius_km?: number | null;
    is_main_hub?: boolean;
    main_hub_distance_km?: number | null;
    configurator_types?: string[];
  }) => {
    const response = await api.post('/operator/locations', data)
    return response.data
  },
  update: async (id: string, data: {
    name?: string;
    address?: string;
    is_active?: boolean;
    service_radius_km?: number | null;
    is_main_hub?: boolean;
    main_hub_distance_km?: number | null;
    configurator_types?: string[];
  }) => {
    const response = await api.put(`/operator/locations/${id}`, data)
    return response.data
  },
  remove: async (id: string) => {
    const response = await api.delete(`/operator/locations/${id}`)
    return response.data
  }
}

// Order API calls
export const orderAPI = {
  createOrder: async (data: {
    project_id: string;
    configuration_ids: string[];
  }): Promise<{
    message: string;
    order: {
      id: string;
      project_id: string;
      user_id: string;
      status: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
      total_price: number;
      created_at: string;
      updated_at: string;
      status_changed_at: string;
      items: Array<{
        id: string;
        order_id: string;
        configuration_id: string;
        price: number;
        quantity: number;
        created_at: string;
        configuration?: any;
      }>;
    };
  }> => {
    const response = await api.post('/orders', data);
    return response.data;
  },

  cancelActiveOrdersForProject: async (projectId: string): Promise<{
    message: string;
    cancelled_count: number;
  }> => {
    const response = await api.post(`/projects/${projectId}/orders/cancel-active`);
    return response.data;
  },

  getOrders: async (params?: {
    status?: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
    project_id?: string;
    page?: number;
    per_page?: number;
  }): Promise<{
    orders: Array<{
      id: string;
      project_id: string;
      user_id: string;
      status: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
      total_price: number;
      created_at: string;
      updated_at: string;
      status_changed_at: string;
      project?: {
        id: string;
        name: string;
      };
      items: Array<{
        id: string;
        order_id: string;
        configuration_id: string;
        price: number;
        quantity: number;
        created_at: string;
        configuration?: {
          id: string;
          name: string;
          configurator_type: string;
          [key: string]: any;
        };
      }>;
    }>;
    pagination: {
      page: number;
      pages: number;
      per_page: number;
      total: number;
      has_next: boolean;
      has_prev: boolean;
    };
  }> => {
    const response = await api.get('/orders', { params });
    return response.data;
  },

  getOrder: async (orderId: string): Promise<{
    order: {
      id: string;
      project_id: string;
      user_id: string;
      status: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
      total_price: number;
      created_at: string;
      updated_at: string;
      status_changed_at: string;
      items: Array<{
        id: string;
        order_id: string;
        configuration_id: string;
        price: number;
        quantity: number;
        created_at: string;
        configuration?: any;
      }>;
    };
  }> => {
    const response = await api.get(`/orders/${orderId}`);
    return response.data;
  },

  updateOrderStatus: async (
    orderId: string,
    status: 'Pending' | 'Processing' | 'Completed' | 'Cancelled'
  ): Promise<{
    message: string;
    order: {
      id: string;
      project_id: string;
      user_id: string;
      status: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
      total_price: number;
      created_at: string;
      updated_at: string;
      status_changed_at: string;
      items: Array<{
        id: string;
        order_id: string;
        configuration_id: string;
        price: number;
        quantity: number;
        created_at: string;
        configuration?: any;
      }>;
    };
  }> => {
    const response = await api.put(`/orders/${orderId}/status`, { status });
    return response.data;
  },

  updateOrderInternalStatus: async (
    orderId: string,
    internalStatus: 'accepted' | 'pending' | 'rejected'
  ): Promise<{
    message: string;
    order: any;
  }> => {
    const response = await api.put(`/orders/${orderId}/internal-status`, { internal_status: internalStatus });
    return response.data;
  },

  deleteOrder: async (orderId: string): Promise<{ message: string }> => {
    const response = await api.delete(`/orders/${orderId}`);
    return response.data;
  },

  getProjectOrders: async (projectId: string): Promise<{
    orders: Array<{
      id: string;
      project_id: string;
      user_id: string;
      status: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
      total_price: number;
      created_at: string;
      updated_at: string;
      status_changed_at: string;
      items: Array<{
        id: string;
        order_id: string;
        configuration_id: string;
        price: number;
        quantity: number;
        created_at: string;
        configuration?: any;
      }>;
    }>;
    project: {
      id: string;
      name: string;
    };
  }> => {
    const response = await api.get(`/projects/${projectId}/orders`);
    return response.data;
  },

  downloadOrderCSV: async (orderId: string): Promise<void> => {
    const response = await api.get(`/orders/${orderId}/download-csv`, {
      responseType: 'blob',
    });
    
    // Create blob link to download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from response headers or create default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `order_${orderId.substring(0, 8)}.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  downloadBulkOrdersCSV: async (filters: {
    statusFilter: string;
    searchTerm: string;
  }): Promise<void> => {
    const response = await api.post(`/orders/download-csv-bulk`, {
      status_filter: filters.statusFilter,
      search_term: filters.searchTerm,
    }, {
      responseType: 'blob',
    });
    
    // Create blob link to download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from response headers or create default
    const contentDisposition = response.headers['content-disposition'];
    let filename = `orders_bulk_export.csv`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  bulkUpdateOrderStatus: async (data: {
    newStatus: 'Pending' | 'Processing' | 'Completed' | 'Cancelled';
    statusFilter: string;
    searchTerm: string;
  }): Promise<{
    message: string;
    updated_count: number;
    total_found: number;
  }> => {
    const response = await api.put('/orders/bulk-update-status', {
      new_status: data.newStatus,
      status_filter: data.statusFilter,
      search_term: data.searchTerm,
    });
    return response.data;
  },
};

export default api; 